import { useCallback, useState } from "react";
import {
  Node,
  NodeProps,
  NodeResizer,
  ResizeDragEvent,
  ResizeParams,
} from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import classNames from "classnames";
import { Text, Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  ImageNodeData,
  isViewOnlyModeAtom,
  isOnlyNodeSelectedAtomFamily,
  isMultiSelectedAtomFamily,
  shouldShowStepIdsAtom,
  saveNodeOnResizeEndSetter,
  areHandlesVisibleAtomFamily,
} from "@zapier/canvas-state";

import { Handles } from "../../../Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { StepId } from "../../../StepId";
import { getNodeResizerHandleStyle, getNodeResizerLineStyle } from "../utils";
import styles from "./ImageNode.module.css";

const ERROR_ICON_SIZE = 18;

export function ImageNode(props: NodeProps<Node<ImageNodeData>>) {
  const {
    id,
    dragging,
    data: { imgUrl },
  } = props;

  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));
  const saveOnResizeEnd = useSetAtom(saveNodeOnResizeEndSetter);
  const [imageError, setImageError] = useState(false);

  const isNodeSelected = !isViewOnlyMode && !dragging && isSelected;
  // Dragging is only true if the node is the only one being dragged (false when multiple nodes are being dragged)
  const isNodeSelectedOrDragged = !isViewOnlyMode && (dragging || isSelected);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleResizeEnd = useCallback(
    (_e: ResizeDragEvent, { width, height, x, y }: ResizeParams) => {
      saveOnResizeEnd({ id, width, height, x, y });
    },
    [saveOnResizeEnd, id],
  );

  if (!imgUrl) {
    return null;
  }

  if (imageError) {
    return (
      <NodeHoverWrapper>
        <div className={styles.container}>
          {shouldShowStepIds && <StepId />}
          <Handles nodeType="image" isVisible={areHandlesVisible} />
          <NodeResizer
            keepAspectRatio={true}
            handleStyle={getNodeResizerHandleStyle()}
            lineStyle={getNodeResizerLineStyle()}
            isVisible={isNodeSelectedOrDragged}
            onResizeEnd={handleResizeEnd}
          />
          <div
            className={classNames(styles.wrapper, {
              [styles.wrapperSingleSelected]: isNodeSelected,
              [styles.wrapperMultiSelected]: isMultiSelected,
            })}
          >
            <div className={styles.errorContainer}>
              <div
                className={styles.errorMessage}
                style={
                  {
                    "--error-icon-size": `${ERROR_ICON_SIZE}px`,
                  } as React.CSSProperties
                }
              >
                <Text type="SmallPrint1" color="TextWeaker">
                  Image failed to load
                </Text>
                <Tooltip
                  content="This may be due to a temporary connection issue or a problem on our end. Try refreshing the page or upload a new image to replace it."
                  placement="bottom left"
                  allowMultiline
                >
                  <TooltipFocusableTrigger>
                    <div className={styles.errorIconWrapper}>
                      <Icon
                        name="alertCircleFill"
                        color="StrokeStronger"
                        size={ERROR_ICON_SIZE}
                      />
                    </div>
                  </TooltipFocusableTrigger>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </NodeHoverWrapper>
    );
  }

  return (
    <NodeHoverWrapper>
      <div className={styles.container}>
        {shouldShowStepIds && <StepId />}
        <Handles nodeType="image" isVisible={areHandlesVisible} />
        <NodeResizer
          keepAspectRatio={true}
          handleStyle={getNodeResizerHandleStyle()}
          lineStyle={getNodeResizerLineStyle()}
          isVisible={isNodeSelectedOrDragged}
          onResizeEnd={handleResizeEnd}
        />
        <div
          className={classNames(styles.wrapper, {
            [styles.wrapperMultiSelected]: isMultiSelected,
          })}
        >
          <img
            src={imgUrl}
            alt="Canvas content"
            className={styles.image}
            onError={handleImageError}
            draggable={false}
          />
        </div>
      </div>
    </NodeHoverWrapper>
  );
}
